import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import NumberField from "../../../Components/NumberField";
import Handler from "../../../Helpers/Handler";
import { currencyFormatter } from "../../../Helpers/Utils";
import { invalidClass, numberError } from "../../../Helpers/bookingRules";

// Manual refund, outside the cancellation policy: goodwill, duplicate charge,
// overcharge, dispute and so on. The server does the real checks (refundable
// balance, idempotency) in PaymentUtils.refundPayment. A refund above the
// site's approval threshold is not sent to Razorpay; it is queued for a
// DIFFERENT admin to approve on the Payments page (Approvals tab).
//
// PATCH /admin/payment/refund/:orderId  { amount, reason, category, speed, idempotencyKey }

const CATEGORIES = [
    { value: "goodwill", label: "Goodwill" },
    { value: "overcharge", label: "Overcharge" },
    { value: "duplicate_charge", label: "Duplicate charge" },
    { value: "damage_waiver", label: "Damage waiver" },
    { value: "dispute", label: "Dispute" },
    { value: "other", label: "Other" },
];

const money = (value) => currencyFormatter(value ?? 0);
const refundableOf = (p) => Math.max(Math.round(((Number(p.totalAmount) || 0) - (Number(p.refundedAmount) || 0)) * 100) / 100, 0);
const CHANNEL = { sdk: "checkout", link: "payment link" };

const RefundModal = ({ payments, onClose, onDone }) => {
    const [orderId, setOrderId] = useState(payments.length === 1 ? payments[0].orderId : "");
    const [amount, setAmount] = useState("");
    const [category, setCategory] = useState("goodwill");
    const [reason, setReason] = useState("");
    const [speed, setSpeed] = useState("normal");
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    // One key per modal session: a double click or a retry after a timeout
    // can never create a second refund.
    const idempotencyKey = useMemo(() => `manual-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, []);

    const selected = payments.find((p) => p.orderId === orderId) || null;
    const refundable = selected ? refundableOf(selected) : 0;

    const validate = () => {
        const next = {
            orderId: orderId ? null : "Choose the payment to refund",
            amount: numberError(amount, { label: "Refund amount", required: true, gt: 0, max: refundable || undefined, maxDecimals: 2 }),
            reason: reason.trim().length >= 3 ? null : "Give a short reason",
        };
        setErrors(next);
        const problem = next.orderId || next.amount || next.reason;
        if (problem) toast.error(problem);
        return !problem;
    };

    const submit = async () => {
        setSubmitting(true);
        const response = await Handler({
            method: "patch",
            url: `/admin/payment/refund/${orderId}`,
            data: { amount: Number(amount), reason: reason.trim(), category, speed, idempotencyKey },
        });
        setSubmitting(false);
        if (response?.success) {
            const data = response?.data?.data || {};
            if (data.status === "pending") toast.info(response?.data?.message || "Sent for approval by another admin");
            else toast.success(`Refund of ${money(data.refundedAmount ?? amount)} processed`);
            onDone?.();
            onClose?.();
        } else {
            setConfirming(false);
            toast.error(response?.data?.message || "Could not process this refund");
        }
    };

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Refund customer</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={submitting}></button>
                        </div>
                        <div className="modal-body">
                            {!confirming ? (
                                <>
                                    <div className="mb-3">
                                        <label className="form-label small">Payment to refund</label>
                                        <select className={`form-select${invalidClass(errors.orderId)}`} value={orderId} onChange={(e) => { setOrderId(e.target.value); setAmount(""); setErrors((p) => ({ ...p, orderId: null })); }} disabled={submitting}>
                                            <option value="">Select payment</option>
                                            {payments.map((p) => (
                                                <option key={p.orderId} value={p.orderId}>
                                                    {p.paymentFor}, paid {money(p.totalAmount)}{CHANNEL[p.channel] ? ` by ${CHANNEL[p.channel]}` : ""}, {money(refundableOf(p))} refundable
                                                </option>
                                            ))}
                                        </select>
                                        {errors.orderId && <div className="invalid-feedback">{errors.orderId}</div>}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label small">Refund amount</label>
                                        <div className="d-flex gap-2">
                                            <NumberField
                                                decimals={2}
                                                min={0}
                                                max={refundable || undefined}
                                                className={`form-control${invalidClass(errors.amount)}`}
                                                value={amount}
                                                onChange={(v) => { setAmount(v); setErrors((p) => ({ ...p, amount: null })); }}
                                                disabled={submitting || !selected}
                                                aria-invalid={!!errors.amount}
                                            />
                                            <button type="button" className="btn btn-outline-secondary btn-sm text-nowrap" disabled={!selected || submitting} onClick={() => setAmount(String(refundable))}>Full balance</button>
                                        </div>
                                        {selected && <div className="form-text">Up to {money(refundable)} can still be refunded on this payment.</div>}
                                        {errors.amount && <div className="text-danger small mt-1">{errors.amount}</div>}
                                    </div>

                                    <div className="row g-2 mb-3">
                                        <div className="col-sm-6">
                                            <label className="form-label small">Category</label>
                                            <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)} disabled={submitting}>
                                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-sm-6">
                                            <label className="form-label small">Speed</label>
                                            <select className="form-select" value={speed} onChange={(e) => setSpeed(e.target.value)} disabled={submitting}>
                                                <option value="normal">Normal (5 to 7 days)</option>
                                                <option value="optimum">Instant when possible (may carry a fee)</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className="form-label small">Reason</label>
                                        <textarea className={`form-control${invalidClass(errors.reason)}`} rows={3} maxLength={300} value={reason} onChange={(e) => { setReason(e.target.value); setErrors((p) => ({ ...p, reason: null })); }} disabled={submitting} placeholder="Why is this refund being made?" />
                                        {errors.reason && <div className="invalid-feedback">{errors.reason}</div>}
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <p className="mb-2">Refund <strong>{money(amount)}</strong> to the customer from {selected?.paymentFor} payment <span className="font-monospace">{selected?.orderId}</span>?</p>
                                    <p className="text-muted small mb-0">Category: {CATEGORIES.find((c) => c.value === category)?.label}. Money goes back to the original payment method and cannot be recalled. A refund above the site's approval limit will wait for a second admin instead of being sent now.</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            {!confirming ? (
                                <>
                                    <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                                    <button type="button" className="btn btn-primary" onClick={() => validate() && setConfirming(true)} disabled={submitting}>Review refund</button>
                                </>
                            ) : (
                                <>
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirming(false)} disabled={submitting}>Back</button>
                                    <button type="button" className="btn btn-danger" onClick={submit} disabled={submitting}>{submitting ? "Refunding..." : `Refund ${money(amount)}`}</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default RefundModal;
